/**
 * Where an emailed sign-in link lands. It signs in only when the customer presses the button,
 * because mail scanners fetch every link in an email and a link that signed in on its own would be
 * spent by the scanner first (AUT-004). The button sends the browser to Better Auth's verify
 * endpoint with the token, which sets the session and redirects to the page the email meant.
 *
 * Better Auth sends a failed link back here with `error` set: INVALID_TOKEN for a link that expired
 * or was already used, any other code for a failure on our side. A link an email client cut short
 * arrives with no token. Each offers a new link, unless the visitor is already signed in, which is
 * common when someone reopens an old email inside a live session. The action area stays empty
 * until the session check answers, so a signed-in visitor never sees the email form flash first.
 *
 * After the first press the Sign in button is held, since a second press would reach the verify
 * endpoint with a token the first one already spent. Coming back with Back releases it.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Mark } from "@lattice/ui/Mark";
import { signedIn } from "./api.ts";
import { EmailCodeSignIn } from "./EmailCodeSignIn.tsx";
type Situation = {
  readonly kind: "ready";
  readonly target: string;
} | {
  readonly kind: "expired" | "failed" | "incomplete";
};
function situation(params: URLSearchParams): Situation {
  const error = params.get("error");
  if (error !== null && error !== "INVALID_TOKEN") return {
    kind: "failed"
  };
  if (error !== null || params.has("expired")) return {
    kind: "expired"
  };
  const token = params.get("token");
  if (!token) return {
    kind: "incomplete"
  };
  const verify = new URLSearchParams({
    token,
    callbackURL: params.get("callbackURL") ?? "/",
    errorCallbackURL: params.get("errorCallbackURL") ?? "/sign-in/confirm?expired=1"
  });
  return {
    kind: "ready",
    target: `/api/auth/magic-link/verify?${verify.toString()}`
  };
}
const COPY = {
  ready: {
    heading: "Finish signing in",
    body: "Press Sign in to confirm it's you. The link works once."
  },
  expired: {
    heading: "This link no longer works",
    body: "Sign-in links work once, within 24 hours. Enter your email and we'll send a code."
  },
  failed: {
    heading: "We couldn't sign you in",
    body: "Something went wrong on our side. Enter your email and we'll send a code."
  },
  incomplete: {
    heading: "This link is incomplete",
    body: "Part of the link was lost on the way. Enter your email and we'll send a code."
  }
} as const;

/** Whether the visitor already has a session: null until the check answers. */
type Session = boolean | null;

/** A failed or slow check falls back to the form after this long rather than holding the page. */
const SESSION_CHECK_MS = 1500;
function useSession(skip: boolean): Session {
  const [session, setSession] = useState<Session>(skip ? false : null);
  useEffect(() => {
    if (skip) return;
    const fallback = window.setTimeout(() => setSession(now => now ?? false), SESSION_CHECK_MS);
    signedIn().then(yes => setSession(yes)).catch(() => setSession(false)).finally(() => window.clearTimeout(fallback));
    return () => window.clearTimeout(fallback);
  }, [skip]);
  return session;
}
function Ready({
  target
}: {
  readonly target: string;
}) {
  const [pressed, setPressed] = useState(false);
  const button = useRef<HTMLDivElement>(null);
  useEffect(() => {
    button.current?.querySelector("button")?.focus();
    /* A page restored from the back-forward cache keeps its state, so a held button would stay held. */
    const release = () => setPressed(false);
    window.addEventListener("pageshow", release);
    return () => window.removeEventListener("pageshow", release);
  }, []);
  return <div ref={button}>
      <Button testId="confirm-sign-in" busy={pressed} wide onClick={() => {
      setPressed(true);
      window.location.assign(target);
    }}>
        {pressed ? "Signing in…" : "Sign in"}
      </Button>
    </div>;
}
function NewSignIn({
  session
}: {
  readonly session: Session;
}) {
  if (session === null) return <div className="x1shn012" aria-busy="true" />;
  if (session) return <LinkButton href="/account">Go to your account</LinkButton>;
  return <EmailCodeSignIn next={null} />;
}
interface Wording {
  readonly heading: string;
  readonly body: string | null;
}
function wording(kind: Situation["kind"], session: Session): Wording {
  if (kind !== "ready" && session === true) {
    return {
      heading: "You're already signed in",
      body: "You don't need this link."
    };
  }
  return COPY[kind];
}
export function ConfirmSignIn() {
  const found = situation(new URLSearchParams(window.location.search));
  const session = useSession(found.kind === "ready");
  const {
    heading,
    body
  } = wording(found.kind, session);
  useEffect(() => {
    document.title = "Sign in to Lattice";
  }, []);
  return <main className="x1ov3xa9 xrvj5dj x1ku5rj1 x1g83aip x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o">
      <div className="x78zum5 xdt5ytf x1qjc9v5 x15agyyk xh8yej3 xowlazn">
        <a href="/" className="x78zum5 x6s0dn4 xqcrz7y x1173whf x10dbofl x1hl2dhg xqu410z x12oqio5 x2jzzdg x1ewu8gn x1de99jn x1t137rt x9v5kkp" aria-label="Lattice home">
          <Mark size={22} />
          <span className="xzb3lse">Lattice</span>
        </a>
        <div className="x78zum5 xdt5ytf x626il7">
          <h1 className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">{heading}</h1>
          {body && <p className="x1ghz6dp x1jty25b x1fzhlzt">{body}</p>}
        </div>
        {found.kind === "ready" ? <Ready target={found.target} /> : <NewSignIn session={session} />}
      </div>
    </main>;
}